import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ScrollToTop } from "./components/ScrollToTop";
import SeasonalEffects from "./components/SeasonalEffects";
import FacebookPixel from "./components/FacebookPixel";
import { lazy, Suspense } from "react";
import ErrorBoundary from "./components/ErrorBoundary";

// Eager load the homepage for best LCP
import Index from "./pages/Index";

// Lazy load all other routes for code splitting
const Login = lazy(() => import("./pages/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const News = lazy(() => import("./pages/News"));
const NewsArticle = lazy(() => import("./pages/NewsArticle"));
const Careers = lazy(() => import("./pages/Careers"));
const Apply = lazy(() => import("./pages/Apply"));
const ApplyNursing = lazy(() => import("./pages/ApplyNursing"));
const ReachUs = lazy(() => import("./pages/ReachUs"));
const RegisterNow = lazy(() => import("./pages/RegisterNow"));
const AgentReferral = lazy(() => import("./pages/AgentReferral"));
const AgentPortal = lazy(() => import("./pages/AgentPortal"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Courses = lazy(() => import("./pages/Courses"));
const CourseDetail = lazy(() => import("./pages/CourseDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ChatWidget = lazy(() => import("./components/chatbot/ChatWidget"));
const ImmigrationServices = lazy(() => import("./pages/ImmigrationServices"));

const queryClient = new QueryClient();

// Minimal loading fallback to prevent layout shift
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <SeasonalEffects />
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <FacebookPixel />
        <ErrorBoundary name="routes">
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/login" element={<Suspense fallback={<PageLoader />}><Login /></Suspense>} />
          <Route path="/dashboard" element={<Suspense fallback={<PageLoader />}><Dashboard /></Suspense>} />
          <Route path="/about" element={<Suspense fallback={<PageLoader />}><AboutUs /></Suspense>} />
          <Route path="/contact" element={<Suspense fallback={<PageLoader />}><ContactUs /></Suspense>} />
          <Route path="/news" element={<Suspense fallback={<PageLoader />}><News /></Suspense>} />
          <Route path="/news/:slug" element={<Suspense fallback={<PageLoader />}><NewsArticle /></Suspense>} />
          <Route path="/careers" element={<Suspense fallback={<PageLoader />}><Careers /></Suspense>} />
          <Route path="/apply" element={<Suspense fallback={<PageLoader />}><Apply /></Suspense>} />
          <Route path="/nursing" element={<Suspense fallback={<PageLoader />}><ApplyNursing /></Suspense>} />
          <Route path="/reachus" element={<Suspense fallback={<PageLoader />}><ReachUs /></Suspense>} />
          <Route path="/register" element={<Suspense fallback={<PageLoader />}><RegisterNow /></Suspense>} />
          <Route path="/agent-portal" element={<Suspense fallback={<PageLoader />}><AgentPortal /></Suspense>} />
          <Route path="/apply/:code" element={<Suspense fallback={<PageLoader />}><AgentReferral /></Suspense>} />
          <Route path="/agent/:code" element={<Suspense fallback={<PageLoader />}><AgentReferral /></Suspense>} />
          <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPolicy /></Suspense>} />
          <Route path="/terms" element={<Suspense fallback={<PageLoader />}><TermsOfService /></Suspense>} />
          <Route path="/courses" element={<Suspense fallback={<PageLoader />}><Courses /></Suspense>} />
          <Route path="/courses/:slug" element={<Suspense fallback={<PageLoader />}><CourseDetail /></Suspense>} />
          <Route path="/immigration-services" element={<Suspense fallback={<PageLoader />}><ImmigrationServices /></Suspense>} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
        </Routes>
        </ErrorBoundary>
        <ErrorBoundary name="chat-widget" silent>
          <Suspense fallback={null}>
            <ChatWidget />
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
